import { useState } from 'react'
import { generateField } from '../../services/ai'
import { useUserSettings } from '../../hooks/useUserSettings'
import { ApiKeyModal } from './ApiKeyModal'
import './GenerateButton.css'

function SparkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12 2l1.2 4.6L18 8l-4.8 1.4L12 14l-1.2-4.6L6 8l4.8-1.4L12 2z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M19 13l.8 3L23 17l-3.2 1-.8 3-.8-3L15 17l3.2-1 .8-3z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Reusable AI generate button for form fields.
 *
 * Props:
 * - fieldLabel: visible label next to the field (e.g. "Description")
 * - fieldKey: form key to generate (e.g. "description")
 * - entityType: 'character' | 'location' | 'event' | 'shot'
 * - entity: current form data (used as context)
 * - onApply: (text) => void
 * - disabled: boolean
 * - extraContext: string | object
 * - projectContext: object (optional) - full project data for enhanced AI generation
 */
export function GenerateButton({
  fieldLabel,
  fieldKey,
  entityType,
  entity,
  onApply,
  disabled = false,
  extraContext,
  projectContext,
  className = '',
}) {
  const [loading, setLoading] = useState(false)
  const [showApiKeyModal, setShowApiKeyModal] = useState(false)
  const { hasOpenAIApiKey, getOpenAIApiKey } = useUserSettings()

  const handleClick = async () => {
    if (loading || disabled) return
    
    // Check if API key is set
    if (!hasOpenAIApiKey()) {
      setShowApiKeyModal(true)
      return
    }
    
    setLoading(true)
    try {
      const text = await generateField({
        fieldLabel,
        fieldKey,
        entityType,
        entity,
        extraContext,
        projectContext,
        apiKey: getOpenAIApiKey(),
      })
      onApply?.(text)
    } catch (err) {
      console.error('AI generate error:', err)
      window.alert(err?.message || 'AI generation failed')
    } finally {
      setLoading(false)
    }
  }

  // Callback when API key is successfully saved - retry generation
  const handleApiKeySaved = () => {
    setShowApiKeyModal(false)
    // Don't auto-retry, let user click again
  }

  return (
    <>
      <button
        type="button"
        className={`generate-btn ${className}`}
        onClick={handleClick}
        disabled={disabled || loading}
        title={loading ? 'Generating…' : `Generate ${fieldLabel}`}
      >
        <span className="generate-btn__icon">
          {loading ? <span className="generate-btn__spinner" /> : <SparkIcon />}
        </span>
        GENERATE
      </button>
      
      <ApiKeyModal
        isOpen={showApiKeyModal}
        onClose={() => setShowApiKeyModal(false)}
        onSuccess={handleApiKeySaved}
      />
    </>
  )
}

export default GenerateButton


